import React, { useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Dialog } from 'primereact/dialog'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'

import 'primereact/resources/themes/lara-dark-indigo/theme.css'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'
import './Profile.css'

import useOrgJwt from '../hooks/useOrgJwt'
import { formatDate } from '../utils/dateFormatter'
import EmptyState from '../components/EmptyState'

const API_BASE    = process.env.REACT_APP_API_BASE_URL || ''
const INGEST_BASE = process.env.REACT_APP_INGEST_BASE_URL || 'https://api.iqtechnologies.ai'

export default function Profile() {
  const { user } = useUser()
  const { orgJwt, tenantId: hookTenantId, role } = useOrgJwt()

  const [tenantId, setTenantId] = useState(hookTenantId || '')
  const [orgName, setOrgName] = useState('')
  const [orgLoading, setOrgLoading] = useState(false)

  const [keys, setKeys] = useState([])
  const [creating, setCreating] = useState(false)
  const [newSecret, setNewSecret] = useState('')
  const [showSecretModal, setShowSecretModal] = useState(false)
  const [copied, setCopied] = useState({ base:false, key:false, tenant:false })
  const [actionBusy, setActionBusy] = useState({})

  useEffect(() => {
    if (!tenantId && orgJwt) {
      try {
        const payload = JSON.parse(atob(orgJwt.split('.')[1]))
        if (payload.tenant_id) setTenantId(payload.tenant_id)
      } catch {}
    }
  }, [orgJwt, tenantId])

  useEffect(() => {
    if (!orgJwt || !tenantId) return
    setOrgLoading(true)
    fetch(`${API_BASE}/organizations/${tenantId}`, {
      headers: { Authorization: `Bearer ${orgJwt}` }
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.name) setOrgName(d.name) })
      .finally(() => setOrgLoading(false))
  }, [orgJwt, tenantId])

  const loadKeys = () => {
    if (!orgJwt || !tenantId) return
    fetch(`${API_BASE}/organizations/${tenantId}/api-keys`, {
      headers: { Authorization: `Bearer ${orgJwt}` }
    })
      .then(r => (r.ok ? r.json() : []))
      .then(list => setKeys(Array.isArray(list) ? list : []))
      .catch(() => setKeys([]))
  }
  useEffect(loadKeys, [orgJwt, tenantId])

  const copy = (field) => {
    let txt = ''
    if (field === 'base') txt = INGEST_BASE
    if (field === 'key')  txt = newSecret
    if (field === 'tenant') txt = tenantId
    if (!txt) return
    navigator.clipboard.writeText(txt)
    setCopied(prev => ({ ...prev, [field]: true }))
    setTimeout(() => setCopied(prev => ({ ...prev, [field]: false })), 1500)
  }

  const createKey = async () => {
    if (!orgJwt || !tenantId) return
    setCreating(true)
    setNewSecret('')
    try {
      const res = await fetch(`${API_BASE}/organizations/${tenantId}/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${orgJwt}`
        },
        body: JSON.stringify({ name: 'Primary' })
      })
      if (!res.ok) throw new Error('Failed to create API key')
      const data = await res.json()
      setNewSecret(data.apiKey)
      setShowSecretModal(true)
      loadKeys()
    } catch (e) {
      console.error(e)
    } finally {
      setCreating(false)
    }
  }

  const revokeKey = async (keyId) => {
    if (!orgJwt) return
    setActionBusy(prev => ({ ...prev, [keyId]: true }))
    try {
      const res = await fetch(`${API_BASE}/organizations/api-keys/${keyId}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${orgJwt}` }
      })
      if (!res.ok) throw new Error('Failed to deactivate key')
      loadKeys()
    } catch (e) {
      console.error(e)
    } finally {
      setActionBusy(prev => ({ ...prev, [keyId]: false }))
    }
  }

  const confirmRevoke = (row) => {
    confirmDialog({
      message: 'Deactivate this API key? Existing clients using this key will stop working.',
      header: 'Confirm Deactivation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Deactivate',
      rejectLabel: 'Cancel',
      acceptClassName: 'p-button-danger',
      accept: () => revokeKey(row.keyId)
    })
  }

  const statusTemplate = (row) => (
    <span className={row.active ? 'status-active' : 'status-inactive'}>
      {row.active ? 'Active' : 'Revoked'}
    </span>
  )

  const actionsTemplate = (row) => (
    <div className="row-actions">
      <Button
        label="Deactivate"
        className="p-button-text p-button-danger p-button-sm"
        disabled={!row.active || actionBusy[row.keyId] || role !== 'admin'}
        loading={!!actionBusy[row.keyId]}
        onClick={() => confirmRevoke(row)}
      />
    </div>
  )

  return (
    <div className="profile-container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <ConfirmDialog />

      <h2>Your Organization</h2>

      <div className="section">
        <h3>General</h3>

        <label className="label-row">
          <span className="label-text">Organization Name</span>
        </label>
        <div className="text-value">
          {orgLoading ? <i className="pi pi-spin pi-spinner" /> : orgName}
        </div>

        <label className="label-row">
          <span className="label-text">
            Tenant ID
            <i
              className="pi pi-copy icon clickable"
              style={{ marginLeft: '.5rem' }}
              onClick={() => copy('tenant')}
              title="Copy Tenant ID"
            />
            {copied.tenant && <span className="copied-text">Copied!</span>}
          </span>
        </label>
        <div className="text-value">{tenantId}</div>
      </div>

      <div className="section">
        <h3>Ingestion</h3>

        <label className="label-row">
          <span className="label-text">
            Base URL
            <i
              className="pi pi-copy icon clickable"
              style={{ marginLeft: '.5rem' }}
              onClick={() => copy('base')}
              title="Copy Base URL"
            />
            {copied.base && <span className="copied-text">Copied!</span>}
          </span>
        </label>
        <div className="text-value">{INGEST_BASE}</div>

        <label className="label-row">
          <span className="label-text">Organization API Keys</span>
        </label>

        {keys.length === 0 && (
          <EmptyState
            icon="pi-key"
            title="No API keys yet"
            description="Generate an API key to start sending logs from your application. Keys are used to authenticate SDK requests."
            variant="subtle"
          />
        )}

        {keys.length > 0 && (
          <div className="keys-table" style={{ width: '100%' }}>
            <DataTable
              value={keys}
              size="small"
              showGridlines
              style={{ width: '100%' }}
            >
              <Column field="name" header="Name" />
              <Column field="prefix" header="Prefix" />
              <Column field="created_at" header="Created" body={(r) => formatDate(r.created_at, 'table')} />
              <Column field="last_used" header="Last Used" body={(r) => r.last_used ? formatDate(r.last_used, 'table') : '—'} />
              <Column header="Status" body={statusTemplate} />
              <Column header="Actions" body={actionsTemplate} />
            </DataTable>
            <p className="note">For security, secrets are not displayed after creation.</p>
          </div>
        )}

        {role === 'admin' && (
          <Button
            label="Generate API Key"
            icon="pi pi-key"
            className="p-button-sm"
            loading={creating}
            onClick={createKey}
          />
        )}

        <p className="note" style={{ marginTop: '1rem' }}>
          SDK usage:
          <br />
          <code>var client = new IQLedgerClient('{INGEST_BASE}', 'keyId.secret');</code>
        </p>
      </div>

      <Dialog
        header="New API Key"
        visible={showSecretModal}
        onHide={() => setShowSecretModal(false)}
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        style={{ width: '42rem' }}
        closable
      >
        <p style={{ marginTop: 0 }}>
          This key is shown <strong>once</strong>. Copy it now and store it securely.
        </p>
        <div className="api-key-once" style={{ wordBreak: 'break-all', padding: '0.5rem 0 1rem 0' }}>
          <span className="jwt-text">{newSecret}</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button
            icon="pi pi-copy"
            label={copied.key ? 'Copied!' : 'Copy'}
            className="p-button-sm"
            onClick={() => copy('key')}
          />
          <Button label="Done" className="p-button-sm" onClick={() => setShowSecretModal(false)} />
        </div>
      </Dialog>
    </div>
  )
}
